import type { Toast } from "react-hot-toast";
import { resolveValue, toast, ToastBar, Toaster as ReactToaster } from "react-hot-toast";

import { Icon } from "@core/components/Icon";
import { IconButton } from "@core/components/IconButton";
import classnames from "classnames";

import { UiCheckCircleIcon } from "@jti/brand-icons/ui-check-circle";
import { UiCrossIcon } from "@jti/brand-icons/ui-cross";
import { UiWarningIcon } from "@jti/brand-icons/ui-warning";

import styles from "./Toaster.module.scss";

export { toast };

const DEFAULT_DURATION = 5000;

const ARIA_PROPS_BY_TYPE: Partial<Record<Toast["type"], Toast["ariaProps"]>> = {
  success: { role: "status", "aria-live": "polite" },
  error: { role: "alert", "aria-live": "assertive" },
};

export type ToasterProps = {
  duration?: number;
  closeButtonLabel?: string;
};

export const Toaster = ({ duration = DEFAULT_DURATION, closeButtonLabel = "" }: ToasterProps) => {
  return (
    <ReactToaster
      toastOptions={{
        duration,
        success: {
          className: classnames(styles.toast, styles.success),
        },
        error: {
          className: classnames(styles.toast, styles.error),
        },
      }}
    >
      {(toastItem) => {
        const isError = toastItem.type === "error";
        const ToneIcon = isError ? UiWarningIcon : UiCheckCircleIcon;
        const resolvedAriaProps = ARIA_PROPS_BY_TYPE[toastItem.type] ?? toastItem.ariaProps;

        return (
          <ToastBar toast={toastItem}>
            {() => (
              <>
                <Icon className={styles.icon} icon={ToneIcon} size="md" />
                <span
                  className={styles.message}
                  role={resolvedAriaProps.role}
                  aria-live={resolvedAriaProps["aria-live"]}
                >
                  {resolveValue(toastItem.message, toastItem)}
                </span>
                <span className={styles.close}>
                  <IconButton
                    aria-label={closeButtonLabel}
                    icon={UiCrossIcon}
                    compact
                    inversed
                    onClick={() => toast.dismiss(toastItem.id)}
                  />
                </span>
              </>
            )}
          </ToastBar>
        );
      }}
    </ReactToaster>
  );
};
